import { useCallback } from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';
import { CustomEventNames, EventTypes, logEvent, logRBIEvent } from 'features/analytics';
import { useSelectStore } from 'features/store/hooks/use-select-store';

import { IRestaurant } from '@rbi-ctg/store';
import { actions, useAppDispatch } from 'state/global-state';
import { StoreCardButtonOptions } from 'state/launchdarkly/variations';
import { routes } from 'utils/routing';

interface IUseStoreActions {
  restaurant: IRestaurant;
}

export const useStoreActions = ({ restaurant }: IUseStoreActions) => {
  const selectStore = useSelectStore();
  const { redirectUrl } = useLocalSearchParams<{ redirectUrl: keyof typeof routes }>();

  const { navigate, canGoBack, back } = useRouter();
  const dispatch = useAppDispatch();

  const logClick = useCallback(
    (name: StoreCardButtonOptions) => {
      logEvent(CustomEventNames.BUTTON_CLICK_STORE_BUTTON, EventTypes.Other, {
        Name: name,
        StoreID: restaurant.number,
      });
    },
    [restaurant]
  );

  const logRBIStoreSelected = useCallback(() => {
    logRBIEvent({
      name: 'Store Selected',
      type: EventTypes.Other,
      attributes: {
        restaurantId: restaurant._id || '',
        restaurantAddress: restaurant.physicalAddress?.address1 || '',
        restaurantCity: restaurant.physicalAddress?.city || '',
        restaurantZip: restaurant.physicalAddress?.postalCode || '',
        restaurantState: restaurant.physicalAddress?.stateProvince || '',
        restaurantCountry: restaurant.physicalAddress?.country || '',
        storeId: restaurant.storeId || '',
      },
    });
  }, [restaurant]);

  /**
   * DEFINE BUTTON CLICK HANDLERS
   */
  const onInfoPress = useCallback(() => {
    logClick(StoreCardButtonOptions.INFO);
    navigate(`${routes.store}/${restaurant._id}`);
  }, [logClick, navigate, restaurant._id]);

  const navigateAfterStoreSelection = useCallback(() => {
    // If we have a redirectUrl, we don't need to navigate anywhere yet. We'll handle that in the useSelectStore hook.
    if (redirectUrl) {
      return;
    }

    if (canGoBack()) {
      back();
    } else {
      navigate(routes.menu);
    }
  }, [redirectUrl, canGoBack, back, navigate]);

  const onOrderPress = useCallback(() => {
    logClick(StoreCardButtonOptions.ORDER);

    selectStore({
      newStore: restaurant,
      callback: () => {
        navigateAfterStoreSelection();
        logRBIStoreSelected();
        // Set shouldRefetchOffers to true on restaurant change
        // this should leave here as we can ensure store has been updated
        dispatch(actions.loyalty.setShouldRefetchOffers(true));
      },
    });
  }, [
    logClick,
    dispatch,
    restaurant,
    selectStore,
    navigateAfterStoreSelection,
    logRBIStoreSelected,
  ]);

  return {
    onOrderPress,
    onInfoPress,
  };
};
